import axios from "axios";
import React, {Component} from "react";
import PropTypes from "prop-types";
import {divIcon} from "leaflet";
import {CircleMarker, GeoJSON, Map, Marker, Popup, TileLayer} from "react-leaflet";
import moment from "moment-timezone";
import {join, map, sortBy, values} from "lodash";

import {firebaseRef} from "config/firebase";
import {Route} from "v2/api/Records";

import "!style!css!leaflet/dist/leaflet.css";


const MAPBOX_URL = (
  `https://api.mapbox.com/styles/v1/mapbox/light-v9/tiles/256/{z}/{x}/{y}?access_token=${process.env.MAPBOX_TOKEN}`
);


class RouteMap extends Component {
  static contextTypes = {
    routeData: PropTypes.instanceOf(Route),
  }

  state = {
    expectedPathData: null,
    mobilePoints: {},
  }

  componentDidMount() {
    // this.loadMobileData();
    this.loadExpectedPathData();
  }

  componentWillReceiveProps(nextProps, nextContext) {
    if (this.context.routeData.id !== nextContext.routeData.id) {
      this.setState({
        expectedPathData: null,
      }, () => this.loadExpectedPathData());
    }
  }

  // expects an array: [[lat, long], [lat,long]...]
  // returns a promise
  getDirections = (points) => {
    const directionsAPI = "https://api.mapbox.com/directions/v5/mapbox/driving";

    const query = join(map(points, (p) => `${p[1]},${p[0]}`), ";");

    return new Promise((resolve, reject) => {
      axios.get(
        `${directionsAPI}/${query}?geometries=geojson&access_token=${process.env.MAPBOX_TOKEN}`
      ).then((resp) => {
        resolve(resp.data);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  getDestinations = () => {
    const {destinations} = this.props;
    return destinations.filter((d) => (
      (d.get("latitude") && d.get("longitude")) || d.get("house")
    ));
  }

  getDestinationCoordinates = (destination) => {
    if (destination.get("latitude") && destination.get("longitude")) {
      return [destination.get("latitude"), destination.get("longitude")];
    }
    const house = destination.get("house");
    return [house.get("latitude"), house.get("longitude")];
  }

  loadExpectedPathData = () => {
    const points = this.getDestinations().map((d) => this.getDestinationCoordinates(d)).toArray();

    this.getDirections(points).then((data) => {
      const route = data && data.routes.length && data.routes[0];
      const expectedPathData = route.geometry;
      this.setState({expectedPathData});
    });
  }

  loadMobileData = () => {
    const {routeData} = this.context;

    const worker = routeData.get("assignments").filter(w => !w.inTraining).first();
    if (!worker || !worker.get("gid")) {
      return;
    }

    firebaseRef.child(`locationsDBv3/${worker.get("gid")}`)
      .orderByChild("timestamp")
      .startAt(moment(routeData.get("date")).valueOf())
      .endAt(moment(routeData.get("date")).add(24, "hours").valueOf())
      .once("value", (data) => {
        const mobilePoints = map(
          sortBy(values(data.val()), (p) => p.timestamp),
          (p) => [p.latitude, p.longitude]
        );
        this.setState({mobilePoints});
      });
  }

  render() {
    const {activeDestination, height, highlightDestination} = this.props;
    const {expectedPathData, mobilePoints} = this.state;
    const destinations = this.getDestinations();
    const destinationPoints = destinations.map(
      dest => this.getDestinationCoordinates(dest)
    ).toArray();

    const markers = destinations.map(dest => {
      const house = dest.get("house");
      const iconClass = String(highlightDestination || activeDestination) === String(dest.get("id")) ?
        "text-warning" : "text-info";
      const icon = divIcon({className: `fa fa-map-marker fa-3x ${iconClass}`});
      return (
        <Marker
          icon={icon}
          key={dest.get("id")}
          position={this.getDestinationCoordinates(dest)}>
          <Popup>
            <div>
              {house &&
                <a
                  href={`${process.env.CRM_URL}/overview/${house.get("id")}`}
                  rel="noopener noreferrer"
                  target="_blank">
                  {house.getIn(["customer", "fullName"])} (ID: {house.get("id")})
                </a>}
              <div className="erg-margin-top-5">
                {house ? house.get("address") : `${dest.get("title")} ${dest.get("address")}`}
              </div>
              <div className="erg-margin-top-5">
                Duration: <b>{moment.duration(dest.get("duration")).asMinutes()} min</b>
              </div>
            </div>
          </Popup>
        </Marker>
      );
    });

    // Path created from mobile GPS data
    const mobileMarkers = map(mobilePoints, (p) => (
      <CircleMarker
        center={p}
        fillColor={"#7BB162"}
        fillOpacity={0.7}
        radius={3}
        weight={0.2} />
    ));

    // expected path taken from directions API
    const expectedPath = expectedPathData && (
      <GeoJSON
        color={"#999"}
        data={expectedPathData}
        opacity={0.6} />
    );

    const style = {
      height,
      width: "100%",
    };

    return (
      <Map bounds={destinationPoints} boundsOptions={{padding: [50, 50]}} style={style}>
        <TileLayer
          attribution="&copy; <a href='http://osm.org/copyright'>OpenStreetMap</a> contributors"
          url={MAPBOX_URL} />
        {markers}
        {mobileMarkers}
        {expectedPath}
      </Map>
    );
  }
}

RouteMap.propTypes = {
  activeDestination: PropTypes.string,
  destinations: PropTypes.object.isRequired,
  height: PropTypes.string,
  highlightDestination: PropTypes.string,
};

export default RouteMap;



/** WEBPACK FOOTER **
 ** ./src/app/v2/components/RouteMap.js
 **/